import { useState, useEffect } from "react";
import axios from "axios";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Settings, Lock, Loader2, Eye, EyeOff, CheckCircle,
  DollarSign, Truck, Plus, Trash2, Save, FileText, History, CreditCard as CardIcon
} from "lucide-react";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

export const AdminSettings = () => {
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [globalSettings, setGlobalSettings] = useState({
    usd_to_htg_rate: 148,
    bvglinkamazon_fee_percent: 0.10,
    amazon_tax_percent: 0.10,
    marketplace_commission_percent: 0.08
  });
  const [shippingCompanies, setShippingCompanies] = useState([]);
  const [newCompany, setNewCompany] = useState({ name: "", price_per_lbs: "" });
  const [ledger, setLedger] = useState([]);
  const [settleForm, setSettleForm] = useState({ company_id: "", amount: "" });

  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [passwordForm, setPasswordForm] = useState({
    current_password: "",
    new_password: "",
    confirm_password: ""
  });

  useEffect(() => {
    fetchSettings();
    fetchShippingCompanies();
    fetchLedger();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/settings`);
      setGlobalSettings(response.data);
    } catch (error) {
      console.error("Error fetching settings:", error);
    } finally {
      setFetching(false);
    }
  };

  const fetchShippingCompanies = async () => {
    try {
      const response = await axios.get(`${API}/admin/shipping`);
      setShippingCompanies(response.data);
    } catch (error) {
      console.error("Error fetching shipping:", error);
    }
  };

  const fetchLedger = async () => {
    try {
      const response = await axios.get(`${API}/admin/shipping/ledger`);
      setLedger(response.data);
    } catch (error) {
      console.error("Error fetching ledger:", error);
    }
  };

  const handleUpdateSettings = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/admin/settings`, globalSettings);
      toast.success("Paramèt yo anrejistre!");
    } catch (error) {
      toast.error("Erè nan anrejistre paramèt");
    } finally {
      setLoading(false);
    }
  };

  const handleAddCompany = async () => {
    if (!newCompany.name || !newCompany.price_per_lbs) return;
    try {
      await axios.post(`${API}/admin/shipping`, {
        name: newCompany.name,
        price_per_lbs: parseFloat(newCompany.price_per_lbs)
      });
      toast.success("Konpayi ajoute!");
      setNewCompany({ name: "", price_per_lbs: "" });
      fetchShippingCompanies();
    } catch (error) {
      toast.error("Erè nan ajoute konpayi");
    }
  };

  const handleDeleteCompany = async (id) => {
    if (!confirm("Ou sèten ou vle efase konpayi sa?")) return;
    try {
      await axios.delete(`${API}/admin/shipping/${id}`);
      toast.success("Konpayi efase");
      fetchShippingCompanies();
      fetchLedger();
    } catch (error) {
      toast.error("Erè nan efase");
    }
  };

  const handleSettle = async (e) => {
    e.preventDefault();
    if (!settleForm.company_id || !settleForm.amount) return;

    setLoading(true);
    try {
      await axios.post(`${API}/admin/shipping/settle`, {
        company_id: settleForm.company_id,
        amount: parseFloat(settleForm.amount),
        notes: "Settle ballast"
      });
      toast.success("Peman anrejistre!");
      setSettleForm({ company_id: "", amount: "" });
      fetchLedger();
    } catch (error) {
      toast.error("Erè nan anrejistre peman");
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error("Nouvo modpas yo pa matche");
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(`${API}/admin/change-password`, {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password
      });
      if (response.data.success) {
        toast.success("Modpas chanje!");
        setPasswordForm({ current_password: "", new_password: "", confirm_password: "" });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erè nan chanje modpas");
    } finally {
      setLoading(false);
    }
  };

  if (fetching) return (
    <AdminLayout>
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#FF9900]" />
      </div>
    </AdminLayout>
  );

  return (
    <AdminLayout>
      <div className="space-y-8" data-testid="admin-settings">
        <h1 className="text-3xl font-bold text-[#232F3E]">Paramèt Platfòm</h1>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Global Defaults */}
          <div className="space-y-8">
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="w-5 h-5 text-[#FF9900]" />
                  Paramèt Pri ak Taux
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleUpdateSettings} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Taux Echanj (1 USD = X HTG)</Label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        type="number"
                        step="0.1"
                        value={globalSettings.usd_to_htg_rate}
                        onChange={(e) => setGlobalSettings({ ...globalSettings, usd_to_htg_rate: parseFloat(e.target.value) })}
                        className="pl-10"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Frè Amazon (%)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={globalSettings.amazon_tax_percent * 100}
                        onChange={(e) => setGlobalSettings({ ...globalSettings, amazon_tax_percent: parseFloat(e.target.value) / 100 })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Komisyon Market (%)</Label>
                      <Input
                        type="number"
                        step="1"
                        value={(globalSettings.marketplace_commission_percent || 0.10) * 100}
                        onChange={(e) => setGlobalSettings({ ...globalSettings, marketplace_commission_percent: parseFloat(e.target.value) / 100 })}
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-[#FF9900] hover:bg-[#F08C00] text-black font-bold"
                    disabled={loading}
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                    Anrejistre Paramèt
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Change Password */}
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Lock className="w-5 h-5 text-[#FF9900]" />
                  Chanje Modpas Admin
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handlePasswordChange} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Modpas Aktyèl</Label>
                    <Input
                      type="password"
                      value={passwordForm.current_password}
                      onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Nouvo Modpas</Label>
                    <Input
                      type="password"
                      value={passwordForm.new_password}
                      onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Konfime Nouvo Modpas</Label>
                    <Input
                      type="password"
                      value={passwordForm.confirm_password}
                      onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                    />
                  </div>
                  <Button type="submit" variant="outline" className="w-full" disabled={loading}>
                    Chanje Modpas
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Shipping Companies */}
          <div className="space-y-8">
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Truck className="w-5 h-5 text-[#FF9900]" />
                  Konpayi Shipping Livrezon
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* List */}
                <div className="space-y-3">
                  {shippingCompanies.length === 0 ? (
                    <p className="text-gray-500 text-center py-4">Pa gen konpayi anrejistre</p>
                  ) : (
                    shippingCompanies.map((co) => (
                      <div key={co.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                        <div>
                          <p className="font-bold text-[#232F3E]">{co.name}</p>
                          <p className="text-sm text-gray-500">${co.price_per_lbs.toFixed(2)} / LBS</p>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          onClick={() => handleDeleteCompany(co.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))
                  )}
                </div>

                {/* Add New */}
                <div className="pt-6 border-t space-y-4">
                  <h4 className="font-semibold text-sm uppercase text-gray-500">Ajoute yon Konpayi</h4>
                  <div className="grid grid-cols-1 gap-4">
                    <div className="space-y-2">
                      <Label>Non Konpayi</Label>
                      <Input
                        placeholder="Ex: DEKO Shipping"
                        value={newCompany.name}
                        onChange={(e) => setNewCompany({ ...newCompany, name: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Prix par LBS (USD)</Label>
                      <Input
                        type="number"
                        placeholder="7.00"
                        value={newCompany.price_per_lbs}
                        onChange={(e) => setNewCompany({ ...newCompany, price_per_lbs: e.target.value })}
                      />
                    </div>
                    <Button
                      onClick={handleAddCompany}
                      className="w-full bg-black text-white hover:bg-gray-800"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Ajoute Konpayi
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Shipping Ledger Section */}
        <div className="pt-8 border-t">
          <h2 className="text-2xl font-bold text-[#232F3E] mb-6 flex items-center gap-2">
            <History className="w-6 h-6 text-[#FF9900]" />
            Ledger Financial Shipping
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Ledger Table */}
            <Card className="lg:col-span-2 border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <FileText className="w-5 h-5 text-[#FF9900]" />
                  Detay Balans yo
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead>
                      <tr className="border-b bg-gray-50">
                        <th className="p-3">Konpayi</th>
                        <th className="p-3">Pwa Total</th>
                        <th className="p-3 text-right">Dwa (USD)</th>
                        <th className="p-3 text-right">Peye (USD)</th>
                        <th className="p-3 text-right">Balans</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ledger.map(row => (
                        <tr key={row.company_id} className="border-b hover:bg-gray-50">
                          <td className="p-3 font-bold">{row.company_name}</td>
                          <td className="p-3">{row.total_lbs} LBS</td>
                          <td className="p-3 text-right text-gray-500">${row.total_accrued_usd.toFixed(2)}</td>
                          <td className="p-3 text-right text-green-600">${row.total_paid_usd.toFixed(2)}</td>
                          <td className="p-3 text-right font-bold text-[#FF9900]">${row.balance_due_usd.toFixed(2)}</td>
                        </tr>
                      ))}
                      {ledger.length === 0 && (
                        <tr><td colSpan="5" className="p-8 text-center text-gray-500">Pa gen done disponib</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* Settlement Form */}
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <CardIcon className="w-5 h-5 text-[#FF9900]" />
                  Anrejistre yon Peman
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSettle} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Konpayi</Label>
                    <select
                      className="w-full p-2 border rounded-md"
                      value={settleForm.company_id}
                      onChange={(e) => setSettleForm({ ...settleForm, company_id: e.target.value })}
                    >
                      <option value="">-- Chwazi --</option>
                      {shippingCompanies.map(co => (
                        <option key={co.id} value={co.id}>{co.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label>Montan Peye (USD)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      value={settleForm.amount}
                      onChange={(e) => setSettleForm({ ...settleForm, amount: e.target.value })}
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full bg-black text-white"
                    disabled={loading || !settleForm.company_id || !settleForm.amount}
                  >
                    Anrejistre Peman
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

